import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import SweetAlert2 from 'react-sweetalert2';
import { Tooltip } from 'react-tooltip';
import { FaArrowAltCircleRight } from 'react-icons/fa';
import SeccionPageHeader from '../Utils/SeccionPageHeader';
import CargandoSpinner from '../Utils/CargandoSpinner';
import { useAuth } from '../../Auth/AuthContext';
import apiService from '../../Auth/apiService';

function ActionButton({ row, onEdit }) {
    return (
        <div className="btn-group">
            <button
                type="button"
                className="btn btn-info"
                onClick={() => onEdit(row)}
                data-tooltip-id="tooltiparriba"
                data-tooltip-content="Editar"
            >
                <FaArrowAltCircleRight />
            </button>
        </div>
    );
}

export default function BaseParametroCrud({
    header,
    formTitle,
    loadUrl,
    saveUrl,
    alertTitle,
    defaultValues,
    mapRowToForm = null,
    columns,
    renderFormFields,
    filter = null,
    rowsPerPageOptions = [5, 10, 20, 50]
}) {
    const { token } = useAuth();
    const [cargando, setCargando] = useState(false);
    const [rows, setRows] = useState([]);
    const [muestraFormulario, setMuestraFormulario] = useState(false);
    const [swalProps, setSwalProps] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    const {
        handleSubmit,
        register,
        setValue,
        getValues,
        watch,
        control,
        reset,
        formState: { errors }
    } = useForm({ defaultValues });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setCargando(true);
            const response = await apiService.get(loadUrl, token);
            setRows(response.data || []);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setCargando(false);
        }
    };

    const Mensaje = (title, text) => {
        setSwalProps({
            show: true,
            title,
            text
        });
    };

    const ResetForm = () => {
        reset(defaultValues);
        setMuestraFormulario(false);
    };

    const Nuevo = () => {
        reset(defaultValues);
        setMuestraFormulario(true);
    };

    const Editar = (row) => {
        const mappedRow = mapRowToForm ? mapRowToForm(row) : row;
        reset({ ...defaultValues, ...mappedRow });
        setMuestraFormulario(true);
    };

    const onSubmit = async (data) => {
        setCargando(true);
        try {
            const response = await apiService.post(saveUrl, data, token);
            if (response.status === 200) {
                Mensaje(alertTitle, JSON.stringify(response.data));
            }
        } catch (error) {
            if (error.status === 422) {
                Mensaje(alertTitle, JSON.stringify(error.response.data.errors));
            } else if (error.status === 409) {
                Mensaje(alertTitle, JSON.stringify(error.response.data));
            }
            console.error('Error saving data:', error);
        } finally {
            setCargando(false);
            fetchData();
            ResetForm();
        }
    };

    const normalizedRows = useMemo(() => {
        if (!filter?.query) return rows;
        const normalize = (value) => (value || '').toString().toLowerCase().trim();
        return rows.filter((row) => normalize(filter.getValue(row)).includes(normalize(filter.query)));
    }, [rows, filter]);

    const totalRows = normalizedRows.length;
    const totalPages = Math.max(1, Math.ceil(totalRows / perPage));
    const paginatedRows = normalizedRows.slice((currentPage - 1) * perPage, currentPage * perPage);

    return (
        <div className="content-wrapper">
            <SeccionPageHeader
                title={header.title}
                idCabezaFamilia={header.idCabezaFamilia || '1'}
                url={header.url}
                nombreurl={header.nombreurl}
                nombreform={header.nombreform}
            />
            <CargandoSpinner Cargando={cargando} />
            <section className="content">
                <div className="container-fluid">
                    {muestraFormulario && (
                        <div className="card card">
                            <div className="card-header">
                                <h3 className="card-title">{formTitle}</h3>
                            </div>
                            <form className="form-horizontal" onSubmit={handleSubmit(onSubmit)}>
                                <div className="card-body">
                                    <input hidden {...register('id')} />
                                    {renderFormFields({ register, errors, setValue, getValues, watch, control, reset })}
                                </div>
                                <div className="card-footer">
                                    <div className="row">
                                        <div className="col-md-1">
                                            <button type="submit" className="btn btn-info">Guardar</button>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    )}

                    <div className="card card">
                        <div className="card-body">
                            <div className="card-body table-responsive p-0">
                                <div className="row">
                                    <div className="col-md-2">
                                        <button type="button" className="form-control" onClick={Nuevo}>Nuevo</button>
                                    </div>
                                </div>

                                <table className="table table-bordered table-hover table-sm">
                                    <thead>
                                        <tr>
                                            <th>Acciones</th>
                                            {columns.map((col) => (
                                                <th key={col.key}>{col.title}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedRows.length === 0 ? (
                                            <tr>
                                                <td colSpan={columns.length + 1} className="text-center">No hay datos para mostrar</td>
                                            </tr>
                                        ) : (
                                            paginatedRows.map((row, idx) => (
                                                <tr key={row.id || idx}>
                                                    <td><ActionButton row={row} onEdit={Editar} /></td>
                                                    {columns.map((col) => (
                                                        <td key={`${col.key}-${row.id || idx}`}>
                                                            {typeof col.render === 'function' ? col.render(row) : row[col.key]}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>

                                <div className="d-flex justify-content-between align-items-center mt-2">
                                    <div>
                                        <label>
                                            Filas por pagina:{' '}
                                            <select
                                                value={perPage}
                                                onChange={(e) => {
                                                    setPerPage(Number(e.target.value));
                                                    setCurrentPage(1);
                                                }}
                                                className="form-control form-control-sm"
                                                style={{ width: 'auto', display: 'inline-block' }}
                                            >
                                                {rowsPerPageOptions.map((n) => (
                                                    <option key={n} value={n}>{n}</option>
                                                ))}
                                            </select>
                                        </label>
                                    </div>
                                    <div>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-secondary"
                                            disabled={currentPage === 1}
                                            onClick={() => setCurrentPage((p) => p - 1)}
                                        >
                                            Anterior
                                        </button>
                                        <span style={{ margin: '0 10px' }}>
                                            Pagina {currentPage} de {totalPages}
                                        </span>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-secondary"
                                            disabled={currentPage >= totalPages}
                                            onClick={() => setCurrentPage((p) => p + 1)}
                                        >
                                            Siguiente
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <SweetAlert2
                {...swalProps}
                didClose={() => {
                    setSwalProps({});
                }}
            />
            <Tooltip id="tooltiparriba" place="right" />
        </div>
    );
}

ActionButton.propTypes = {
    row: PropTypes.object.isRequired,
    onEdit: PropTypes.func.isRequired
};

BaseParametroCrud.propTypes = {
    header: PropTypes.shape({
        title: PropTypes.string.isRequired,
        idCabezaFamilia: PropTypes.string,
        url: PropTypes.string.isRequired,
        nombreurl: PropTypes.string.isRequired,
        nombreform: PropTypes.string.isRequired
    }).isRequired,
    formTitle: PropTypes.string.isRequired,
    loadUrl: PropTypes.string.isRequired,
    saveUrl: PropTypes.string.isRequired,
    alertTitle: PropTypes.string.isRequired,
    defaultValues: PropTypes.object.isRequired,
    mapRowToForm: PropTypes.func,
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired,
            render: PropTypes.func
        })
    ).isRequired,
    renderFormFields: PropTypes.func.isRequired,
    filter: PropTypes.shape({
        query: PropTypes.string,
        getValue: PropTypes.func
    }),
    rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number)
};